import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/vue';
import App from './App.vue';
import router from './router';
import './style.css';

// Initialize PostHog analytics (only when key is configured)
// Requests route through /ingest on our own domain to bypass ad blockers
if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: `${window.location.origin}/ingest`,
    ui_host: 'https://us.posthog.com',
    autocapture: true,
    capture_pageview: false,  // handled via router.afterEach
    // Keep ALL PostHog state (distinct_id, session ID, device ID) in one
    // cookie on .etaiota.org, shared with www.etaiota.org / etaiota.org, so
    // moving between the sites continues the same person and session. The
    // default "localStorage+cookie" also writes that cookie, but on load each
    // origin's own localStorage overrides it, so switching sites resumed a
    // stale per-origin session instead. Must match etaiota-site's
    // _includes/base.njk posthog.init.
    persistence: 'cookie',
    cross_subdomain_cookie: true,
    session_recording: {
      maskAllInputs: false,          // record all typed text to aid debugging
      maskInputOptions: { password: true },  // except passwords
      captureCanvas: { recordCanvas: true },  // capture <canvas> content (e.g. the Family Tree chart) in replays
    },
  });
  posthog.register({ environment: import.meta.env.VITE_ENVIRONMENT ?? 'development' });
}

// Configure AWS Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID || '',
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID || '',
      loginWith: {
        email: true,
      },
    },
  },
});

// Share the signed-in session with etaiota-site (www.etaiota.org) by storing
// auth tokens in a cookie scoped to the shared parent domain instead of
// Amplify's default per-origin localStorage. See
// etaiota-site/docs/member-auth-plan.md for the full rationale -- this is
// what makes "already logged in on either site" and "log out of both at
// once" work, without needing Amplify's heavier signOut({ global: true }).
//
// Guarded to real *.etaiota.org origins only: a cookie scoped to
// .etaiota.org can't be set from localhost (the browser rejects it), so
// forcing this unconditionally would silently break local dev sign-in.
// Local dev keeps Amplify's default storage instead.
if (window.location.hostname.endsWith('.etaiota.org')) {
  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage({
      domain: '.etaiota.org',
      path: '/',
      expires: 30, // days -- matches this app client's refreshTokenValidity
      secure: true,
      sameSite: 'lax',
    })
  );
}

const app = createApp(App);

// Initialize Sentry error tracking (production only — DSN not set in dev/test)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: 1.0,        // 100% while traffic is low; reduce at scale
    replaysOnErrorSampleRate: 1.0, // session replay on every error
    environment: import.meta.env.VITE_ENVIRONMENT ?? 'development',

    // Suppress expected errors that are not actionable bugs
    ignoreErrors: [
      // Auth flows — expected user-facing outcomes, not bugs
      'This account has already been claimed',
      'Incorrect username or password',
      'There is already a signed in user',
      // AI search — graceful 400 when Claude can't parse the query
      'Could not interpret query',
      // Activation — user entered an expired or wrong code; shown to user, not a bug
      'Invalid verification code',
      // Admin member edit — member has accountClaimed flag but no Cognito role; handled gracefully
      'Member does not have a claimed account',
      // Browser extension noise (LastPass, password managers injecting into the DOM)
      'Object Not Found Matching Id',
      // Stale chunk errors after deploys — handled by router.onError reload instead
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      'Unable to preload CSS',
      /is not a valid JavaScript MIME type/,
    ],
  });
}

// Catch Vite preload errors (stale chunks after deploy) that aren't caught by router.onError
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

app.use(createPinia());
app.use(router);

app.mount('#app');
